import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { numCoerce } from '../coerce.js';

// ---------------------------------------------------------------------------
// WCAG 2.1 Color Math
// ---------------------------------------------------------------------------

/**
 * Linearize one 8-bit sRGB channel (0–255) per WCAG 2.1 relative luminance spec.
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export function linearizeChannel(c255: number): number {
  const c = c255 / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * WCAG 2.1 relative luminance for an RGB triplet (values 0–255).
 */
export function relativeLuminance(r: number, g: number, b: number): number {
  return 0.2126 * linearizeChannel(r) + 0.7152 * linearizeChannel(g) + 0.0722 * linearizeChannel(b);
}

/**
 * WCAG 2.1 contrast ratio between two luminance values.
 * Always returns a value in [1, 21].
 */
export function contrastRatioFromLuminance(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

// ---------------------------------------------------------------------------
// Color Parsing
// ---------------------------------------------------------------------------

type RGB = [number, number, number];

/**
 * Parse a hex color string (#RGB or #RRGGBB) to [r, g, b] (0–255).
 * Returns null on invalid input.
 */
export function parseHex(hex: string): RGB | null {
  const clean = hex.replace('#', '').trim().toLowerCase();
  if (clean.length === 3) {
    const r = parseInt(clean.charAt(0) + clean.charAt(0), 16);
    const g = parseInt(clean.charAt(1) + clean.charAt(1), 16);
    const b = parseInt(clean.charAt(2) + clean.charAt(2), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return [r, g, b];
  }
  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return [r, g, b];
  }
  return null;
}

/**
 * Convert HSL (h: 0–360, s: 0–100, l: 0–100) to RGB (0–255).
 */
export function hslToRgb(h: number, s: number, l: number): RGB {
  const hn = h / 360;
  const sn = s / 100;
  const ln = l / 100;

  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  let r: number, g: number, b: number;
  if (sn === 0) {
    r = g = b = ln;
  } else {
    const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
    const p = 2 * ln - q;
    r = hue2rgb(p, q, hn + 1 / 3);
    g = hue2rgb(p, q, hn);
    b = hue2rgb(p, q, hn - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Parse an HSL string. Supports:
 * - "hsl(H, S%, L%)"          — CSS function format
 * - "H S% L%"                  — shadcn CSS-variable format (e.g. "222.2 47.4% 11.2%")
 * - "H, S%, L%"                — comma-separated without wrapper
 * Returns null on invalid input.
 */
export function parseHsl(hsl: string): RGB | null {
  // Strip hsl() wrapper if present
  let str = hsl
    .replace(/^hsl\s*\(\s*/i, '')
    .replace(/\s*\)$/, '')
    .trim();
  // Strip % signs, then split on commas and/or whitespace
  str = str.replace(/%/g, '');
  const parts = str.split(/[\s,]+/).filter(Boolean);
  if (parts.length !== 3) return null;

  // Length is already verified === 3 above; non-null assertions are safe.
  const h = parseFloat(parts[0]!);
  const s = parseFloat(parts[1]!);
  const l = parseFloat(parts[2]!);
  if (isNaN(h) || isNaN(s) || isNaN(l)) return null;

  return hslToRgb(h, s, l);
}

/**
 * Parse any supported color format to [r, g, b] (0–255).
 * Supported formats: #RGB, #RRGGBB, hsl(...), shadcn "H S% L%".
 * Returns null on unrecognized format.
 */
export function parseColor(color: string): RGB | null {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) return parseHex(trimmed);
  if (/^hsl\s*\(/i.test(trimmed)) return parseHsl(trimmed);
  // shadcn format: digits, then optional whitespace/comma, then digits+%, then digits+%
  if (/^\d[\d.]*[\s,]+\d[\d.]*%?[\s,]+\d[\d.]*%?$/.test(trimmed)) return parseHsl(trimmed);
  return null;
}

/**
 * Convert an RGB triplet (0–255) to a lowercase hex string.
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((c) =>
        Math.max(0, Math.min(255, Math.round(c)))
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  );
}

// ---------------------------------------------------------------------------
// Exported Handler Functions (pure — testable without MCP server)
// ---------------------------------------------------------------------------

export interface ContrastResult {
  fgHex: string;
  bgHex: string;
  ratio: number;
  /** Normal text (≥4.5:1) */
  aa: boolean;
  /** Large text or UI components (≥3.0:1) */
  aaLarge: boolean;
  /** Normal text enhanced (≥7.0:1) */
  aaa: boolean;
  /** Large text enhanced (≥4.5:1) — same threshold as normal AA */
  aaaLarge: boolean;
}

export function handleCheckContrast(fg: string, bg: string): ContrastResult {
  const fgRgb = parseColor(fg);
  const bgRgb = parseColor(bg);
  if (!fgRgb) throw new Error(`Cannot parse foreground color: "${fg}"`);
  if (!bgRgb) throw new Error(`Cannot parse background color: "${bg}"`);

  const fgL = relativeLuminance(...fgRgb);
  const bgL = relativeLuminance(...bgRgb);
  const ratio = parseFloat(contrastRatioFromLuminance(fgL, bgL).toFixed(2));

  return {
    fgHex: rgbToHex(...fgRgb),
    bgHex: rgbToHex(...bgRgb),
    ratio,
    aa: ratio >= 4.5,
    aaLarge: ratio >= 3.0,
    aaa: ratio >= 7.0,
    aaaLarge: ratio >= 4.5,
  };
}

export type PaletteStrategy = 'white' | 'black' | 'unachievable';

export interface PaletteResult {
  seedHex: string;
  foreground: string;
  ratio: number;
  aa: boolean;
  aaLarge: boolean;
  aaa: boolean;
  meetsTarget: boolean;
  strategy: PaletteStrategy;
  alternativeForeground: string;
  alternativeRatio: number;
}

/**
 * Given a seed color (treated as a background), find the accessible foreground.
 * Tries #ffffff and #000000; picks whichever satisfies targetRatio and has the
 * higher contrast. If both satisfy the target the higher-contrast option wins.
 * If neither can satisfy the target (only possible for targetRatio > 21) the
 * best available option is returned with meetsTarget: false.
 */
export function handleDeriveAccessiblePalette(seed: string, targetRatio: number): PaletteResult {
  const seedRgb = parseColor(seed);
  if (!seedRgb) throw new Error(`Cannot parse seed color: "${seed}"`);

  const seedHex = rgbToHex(...seedRgb);
  const seedL = relativeLuminance(...seedRgb);

  const whiteRatio = parseFloat(contrastRatioFromLuminance(1.0, seedL).toFixed(2));
  const blackRatio = parseFloat(contrastRatioFromLuminance(0.0, seedL).toFixed(2));

  const whiteWorks = whiteRatio >= targetRatio;
  const blackWorks = blackRatio >= targetRatio;

  let strategy: PaletteStrategy;
  let foreground: string;
  let ratio: number;
  let alternativeForeground: string;
  let alternativeRatio: number;

  if (blackWorks && whiteWorks) {
    // Both work — prefer whichever gives more contrast
    if (blackRatio >= whiteRatio) {
      strategy = 'black';
      foreground = '#000000';
      ratio = blackRatio;
      alternativeForeground = '#ffffff';
      alternativeRatio = whiteRatio;
    } else {
      strategy = 'white';
      foreground = '#ffffff';
      ratio = whiteRatio;
      alternativeForeground = '#000000';
      alternativeRatio = blackRatio;
    }
  } else if (blackWorks) {
    strategy = 'black';
    foreground = '#000000';
    ratio = blackRatio;
    alternativeForeground = '#ffffff';
    alternativeRatio = whiteRatio;
  } else if (whiteWorks) {
    strategy = 'white';
    foreground = '#ffffff';
    ratio = whiteRatio;
    alternativeForeground = '#000000';
    alternativeRatio = blackRatio;
  } else {
    // Neither meets target (only reachable for pathological targetRatio > 21)
    strategy = 'unachievable';
    if (blackRatio >= whiteRatio) {
      foreground = '#000000';
      ratio = blackRatio;
      alternativeForeground = '#ffffff';
      alternativeRatio = whiteRatio;
    } else {
      foreground = '#ffffff';
      ratio = whiteRatio;
      alternativeForeground = '#000000';
      alternativeRatio = blackRatio;
    }
  }

  return {
    seedHex,
    foreground,
    ratio,
    aa: ratio >= 4.5,
    aaLarge: ratio >= 3.0,
    aaa: ratio >= 7.0,
    meetsTarget: ratio >= targetRatio,
    strategy,
    alternativeForeground,
    alternativeRatio,
  };
}

// ---------------------------------------------------------------------------
// MCP Tool Registration
// ---------------------------------------------------------------------------

const PASS = '[PASS]';
const FAIL = '[FAIL]';
const mark = (ok: boolean) => (ok ? PASS : FAIL);

export function registerContrastTools(server: McpServer): void {
  // -------------------------------------------------------------------------
  // stackwright_pro_check_contrast
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_check_contrast',
    'Compute the exact WCAG 2.1 contrast ratio between a foreground and background color. ' +
      'Returns the ratio and AA/AAA pass/fail flags. ' +
      'Use this to verify any foreground/background color pair before writing it to the token set. ' +
      'Accepts #RGB, #RRGGBB, hsl(...), or shadcn HSL format ("H S% L%").',
    {
      fg: z.string().describe('Foreground (text) color — hex or HSL'),
      bg: z.string().describe('Background color — hex or HSL'),
    },
    async ({ fg, bg }) => {
      let result: ContrastResult;
      try {
        result = handleCheckContrast(fg, bg);
      } catch (err) {
        return {
          content: [{ type: 'text' as const, text: `Error: ${(err as Error).message}` }],
        };
      }

      const text = [
        `WCAG 2.1 Contrast Check`,
        ``,
        `  Foreground : ${result.fgHex}`,
        `  Background : ${result.bgHex}`,
        `  Ratio      : ${result.ratio}:1`,
        ``,
        `  ${mark(result.aa)}  AA  — normal text  (≥4.5:1)`,
        `  ${mark(result.aaLarge)}  AA  — large text   (≥3.0:1)`,
        `  ${mark(result.aaa)}  AAA — normal text  (≥7.0:1)`,
        `  ${mark(result.aaaLarge)}  AAA — large text   (≥4.5:1)`,
      ].join('\n');

      return { content: [{ type: 'text' as const, text }] };
    }
  );

  // -------------------------------------------------------------------------
  // stackwright_pro_derive_accessible_palette
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_derive_accessible_palette',
    'Given a seed color (treated as a background), derive the best accessible foreground ' +
      '(text) color that meets the target contrast ratio. ' +
      'Evaluates #ffffff and #000000 against the seed and picks whichever satisfies the ' +
      'target ratio with the highest contrast. ' +
      'Use this for every {name}-foreground token derivation instead of guessing white vs black. ' +
      'Accepts #RGB, #RRGGBB, hsl(...), or shadcn HSL format ("H S% L%").',
    {
      seed: z.string().describe('Background (seed) color — hex or HSL'),
      targetRatio: numCoerce(z.number().default(4.5)).describe(
        'Minimum required contrast ratio (default 4.5 for WCAG AA)'
      ),
    },
    async ({ seed, targetRatio }) => {
      let result: PaletteResult;
      try {
        result = handleDeriveAccessiblePalette(seed, targetRatio ?? 4.5);
      } catch (err) {
        return {
          content: [{ type: 'text' as const, text: `Error: ${(err as Error).message}` }],
        };
      }

      const metLine = result.meetsTarget
        ? `${PASS} Meets target (${targetRatio}:1)`
        : `${FAIL} Target (${targetRatio}:1) UNACHIEVABLE with white or black — best available used`;

      const altNote =
        result.alternativeRatio >= (targetRatio ?? 4.5)
          ? `also passes (${result.alternativeRatio}:1)`
          : `fails at ${result.alternativeRatio}:1`;

      const text = [
        `Accessible Palette Derivation`,
        ``,
        `  Background (seed) : ${result.seedHex}`,
        `  Foreground        : ${result.foreground}  (${result.strategy})`,
        `  Contrast ratio    : ${result.ratio}:1`,
        `  ${metLine}`,
        ``,
        `  ${mark(result.aa)}  AA  — normal text  (≥4.5:1)`,
        `  ${mark(result.aaLarge)}  AA  — large text   (≥3.0:1)`,
        `  ${mark(result.aaa)}  AAA — normal text  (≥7.0:1)`,
        ``,
        `  Alternative (${result.alternativeForeground}): ${altNote}`,
      ].join('\n');

      return { content: [{ type: 'text' as const, text }] };
    }
  );
}
